// Platform connections management page for managing brand social media and blog platform integrations with enhanced visual design
import { useParams, useSearchParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Bot } from 'lucide-react';
import { useBrands } from '@/lib/BrandsProvider';
import { useEffect } from 'react';

// Import the existing connections functionality
import ConnectionsTab from './tabs/ConnectionsTab';

export const PlatformConnections: React.FC = () => {
  const { id: brandId } = useParams<{ id: string }>();
  const { selectedBrand } = useBrands();
  const [searchParams, setSearchParams] = useSearchParams();
  
  
  // Check for platform authentication parameters
  useEffect(() => {
    const platform = searchParams.get('platform');
    const action = searchParams.get('action');
    
    if (platform && brandId) {
      // If we have platform and brand_id parameters, we're returning from an OAuth flow
      console.log(`Detected return from ${platform} authentication flow for brand ${brandId}`);
      
      // Remove the platform parameter to avoid reprocessing
      if (['linkedin', 'twitter', 'facebook', 'instagram', 'tiktok', 'wordpress', 'medium', 'linkedin_article'].includes(platform)) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          // Remove the platform parameter to avoid reprocessing
          newParams.delete('platform');
          // If action=add is present, keep it so the dialog can open once
          if (action !== 'add') {
            newParams.delete('action');
          }
          return newParams;
        });
      }
    }
  }, [searchParams, brandId, setSearchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="relative container mx-auto p-6 max-w-6xl">
        
        {/* Enhanced Header Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-xl border border-blue-200/50 shadow-sm mb-6 p-6">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16 pointer-events-none" />
          <div className="relative">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg">
                  <Bot className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-1">
                    Social Automation Agents
                  </h1>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Connect and manage your {selectedBrand?.brand_name || 'brand'} social media and blog platform integrations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Connections Content */}
        <div className="space-y-6">
          {brandId && (
            <ConnectionsTab key={brandId} brandId={brandId} />
          )}
        </div>
        
        <Toaster />
      </div>
    </div>
  );
};

export default PlatformConnections; 